import { AxiosResponse } from 'axios';
import { ISearchQueryParams } from '../types/utils.types';

export const loadScriptonDemand = (
  scriptUrl: string | undefined,
  onLoad: EventListener
) => {
  if (scriptUrl) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = scriptUrl;
    const headScript = document.getElementsByTagName('script')[0];
    if (headScript.parentNode) {
      headScript.parentNode.insertBefore(script, headScript);
    }
    script.addEventListener('load', onLoad);
  }
};

export const truncateMiddle = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }

  const ellipsis = '...';
  const startLength = Math.floor((maxLength - ellipsis.length) / 2);
  const endLength = Math.ceil((maxLength - ellipsis.length) / 2);

  const truncatedText =
    text.slice(0, startLength) + ellipsis + text.slice(-endLength);

  return truncatedText;
};

export const secondsToHMS = (seconds: number) => {
  const hour = Math.floor(seconds / 3600);
  const minute = Math.floor((seconds % 3600) / 60);
  const second = Math.floor((seconds % 3600) % 60);

  return {
    hour,
    minute,
    second,
  };
};

/**
 * Query Params Serialization
 * @param {ISearchQueryParams} srcObj:ISearchQueryParams
 * @returns {void}
 */
export const paramSerialization = (srcObj: ISearchQueryParams) => {
  return Object.keys(srcObj).reduce((newObj, key: string) => {
    // this value can be any type ex: string, number, array object etc., hence used type any
    const value: any = srcObj[key];
    if (
      (value !== undefined && value !== null && value !== '') ||
      (typeof value === 'number' && !isNaN(value)) ||
      (Array.isArray(value) && value.length > 0) ||
      (value !== null &&
        typeof value === 'object' &&
        Object.keys(value) != null &&
        Object.keys(value).length > 0)
    ) {
      newObj[key] = value;
    }
    return newObj;
  }, {} as ISearchQueryParams);
};

type ObjectWithProperties = { [key: string]: any };

/**
 * Description
 * @param {T[]} sourceArray:T[]
 * @param {Array} propertyMappings:Array<{oldName:string;newName:string}>
 * @returns {void}
 */
export function renameObjectProperties<T extends ObjectWithProperties>(
  sourceArray: T[],
  propertyMappings: Array<{ oldName: string; newName: string }>
): T[] {
  return sourceArray.map((obj) => {
    const newObj: ObjectWithProperties = {};

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const mapping = propertyMappings.find((m) => m.oldName === key);
        const newKey = mapping ? mapping.newName : key;
        newObj[newKey] = obj[key];
      }
    }

    return newObj as T;
  });
}

// Ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals
export function template(
  strings: TemplateStringsArray,
  ...keys: (string | number)[]
) {
  return (...values: any[]) => {
    const dict = values[values.length - 1] || {};
    const result: (string | number)[] = [strings[0]];
    keys.forEach((key, i) => {
      const value = Number.isInteger(key)
        ? values[key as number]
        : dict[key as string];
      result.push(value, strings[i + 1]);
    });
    return result.join('');
  };
}

export async function copyTextToClipboard(text: string) {
  if ('clipboard' in navigator) {
    return navigator.clipboard.writeText(text ?? '');
  } else {
    return document.execCommand('copy', true, text);
  }
  // if (navigator.clipboard) {
  //   navigator.clipboard
  //     .writeText(text)
  //     .then(() => {
  //       window.alert('copied');
  //       showAlert({
  //         show: true,
  //         message: 'Text copied to clipboard',
  //         type: EAlertType.SUCCESS,
  //       });
  //     })
  //     .catch((err) => {
  //       showAlert({
  //         show: true,
  //         message: err.message || 'Unable to copy text',
  //         type: EAlertType.ERROR,
  //       });
  //     });
  // } else {
  //   const input = document.createElement('input');
  //   input.value = text;
  //   document.body.appendChild(input);
  //   input.select();
  //   document.execCommand('copy');
  //   document.body.removeChild(input);
  // }
}
export const getTitleCase = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const debounceFn = (func: (value: string) => void, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (args: string) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(args);
    }, delay * 1000);
  };
};

export const snakeCase = (word: string) => {
  return word
    .replace(/\W+/g, ' ')
    .split(/ |\B(?=[A-Z])/)
    .map((character) => character.toLowerCase())
    .join('_');
};

export const findArrayObjectDiff = <T>(oldArray: T[], newArray: T[]) => {
  // Objects to store differences
  const added: T[] = [];
  const removed: T[] = [];

  // Set for efficient lookup of objects in new array
  const newObjectSet: Set<string> = new Set(
    newArray.map((obj) => JSON.stringify(obj))
  );

  // Loop through old array
  for (const oldObj of oldArray) {
    const oldObjString = JSON.stringify(oldObj);

    // If object not found in new array, it's removed
    if (!newObjectSet.has(oldObjString)) {
      removed.push(oldObj);
    }
  }

  // Loop through new array
  for (const newObj of newArray) {
    const newObjString = JSON.stringify(newObj);

    // If object not found in old array (using set lookup for efficiency), it's added
    if (!oldArray.some((oldObj) => JSON.stringify(oldObj) === newObjString)) {
      added.push(newObj);
    }
  }

  return { added, removed };
};

// Email validation utility
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};
/**
 * Description
 * @param {string} word:string
 * @returns {string}
 */
export const firstLetterUpperCase = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

/**
 * A function to convert date to local ISO format
 * @param {Date} date:Date
 * @returns {string}
 */

export const getLocalISODate = (date = new Date()) => {
  const pad = (n: any) => String(n).padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // Months are 0-indexed
  const day = pad(date.getDate());

  return `${year}-${month}-${day}`;
};

export const downloadFile = (
  response: AxiosResponse,
  contentType: string,
  filename: string
) => {
  const blob = new Blob([response.data], {
    type: contentType,
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};
